import { FormControlLabel, Switch } from '@mui/material';
import { FilterKeys, GetParentsQueryFilters } from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  DateSelect,
  FiltersSidebar,
  FiltersSidebarMethods,
  GenderSelectMultiple,
  InviteStatusSelectMultiple,
  NationalitySelectMultiple,
  PropertyTypeSelectMultiple,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { forwardRef, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useParentsFilters } from './useParentsFilters';

type ParentsFiltersSidebarProps = {
  filters: GetParentsQueryFilters;
  defaultFilters: GetParentsQueryFilters;
  schoolId: string;
  onApply: (v: GetParentsQueryFilters) => void;
};

export const ParentsFiltersSidebar = forwardRef<FiltersSidebarMethods, ParentsFiltersSidebarProps>(
  ({ filters: propsFilters, defaultFilters, onApply, schoolId }, ref) => {
    const [filters, setFilters] = useState(propsFilters);
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const {
      labels,
      hasHouses,
      filtersHouse,
      filtersGender,
      filtersNationality,
      filtersStatus,
      filtersAgeGroup,
      filtersInviteStatus,
      filtersOnlyEmptyEmail,
      onSetNationality,
      onClearNationality,
      onSetGender,
      onClearGender,
      onSetHouseId,
      onClearHouse,
      onSetStatusId,
      onClearStatus,
      onSetInviteStatus,
      onSetOnlyEmptyEmail,
      onClearInviteStatus,
      onSetDate,
      filtersDate,
      onSetAgeGroup,
      onClearAgeGroup,
    } = useParentsFilters({ schoolId, filters, onSetFilters: setFilters });
    const { formatMessage } = useIntl();
    const isOnlyEmptyEmail = !!filtersOnlyEmptyEmail?.[0];
    const handleApply = useCallback(() => {
      onApply(filters);
    }, [filters, onApply]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
    }, [defaultFilters]);

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Parents' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <DateSelect
          onSetDate={onSetDate}
          date={filtersDate}
          placeholder={labels[FilterKeys.Date]}
          requiredLabel="required"
          emptyValue={formatMessage({ id: 'datepicker-Today' })}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <PropertyTypeSelectMultiple
          placeholder={labels[FilterKeys.Status]}
          allowArchived
          userRole={SchoolUserRole.Student}
          schoolId={schoolId}
          onSelectId={(v: string) =>
            onSetStatusId(toggleMultipleValueArrayProperty(filtersStatus, [v]))
          }
          selectedIds={filtersStatus || []}
          onClear={onClearStatus}
          propertyType={SchoolPropertyType.Status}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <AgeGroupSelectMultiple
          allowArchived
          userRole={SchoolUserRole.Student}
          placeholder={labels[FilterKeys.AgeGroup]}
          schoolId={schoolId}
          onSelect={(v) => onSetAgeGroup(toggleMultipleValueArrayProperty(filtersAgeGroup, v))}
          selectedIds={filtersAgeGroup || []}
          onClear={onClearAgeGroup}
          onOpen={preventClose}
          onClose={allowClose}
        />

        {hasHouses && (
          <PropertyTypeSelectMultiple
            placeholder={labels[FilterKeys.House]}
            allowArchived
            userRole={SchoolUserRole.Student}
            schoolId={schoolId}
            onSelectId={(v: string) =>
              onSetHouseId(toggleMultipleValueArrayProperty(filtersHouse, [v]))
            }
            selectedIds={filtersHouse || []}
            onClear={onClearHouse}
            propertyType={SchoolPropertyType.House}
            onOpen={preventClose}
            onClose={allowClose}
          />
        )}

        <GenderSelectMultiple
          placeholder={labels[FilterKeys.Gender]}
          onSelectGender={(v) => onSetGender(toggleMultipleValueArrayProperty(filtersGender, [v]))}
          selectedGenders={filtersGender || []}
          onClear={onClearGender}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <NationalitySelectMultiple
          placeholder={labels[FilterKeys.Nationality]}
          onSelectNationality={(v) =>
            onSetNationality(toggleMultipleValueArrayProperty(filtersNationality, [v]))
          }
          selectedNationalities={filtersNationality || []}
          onClear={onClearNationality}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <InviteStatusSelectMultiple
          placeholder={labels[FilterKeys.InviteStatus]}
          onSelectStatus={(v) =>
            onSetInviteStatus(toggleMultipleValueArrayProperty(filtersInviteStatus, [v]))
          }
          selectedStatuses={filtersInviteStatus || []}
          onClear={onClearInviteStatus}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <FormControlLabel
          sx={{
            px: 1.5,
          }}
          control={
            <Switch
              checked={isOnlyEmptyEmail}
              // biome-ignore lint/complexity/noUselessTernary: Legacy code
              onChange={() => onSetOnlyEmptyEmail(isOnlyEmptyEmail ? false : true)}
            />
          }
          label={formatMessage({ id: 'peopleDetail-NoEmail' })}
        />
      </FiltersSidebar>
    );
  },
);
